import { createContext, useContext, type ReactNode } from "react";
import type { CheckboxProps } from "./Checkbox";
import Checkbox from "./Checkbox";
import type { ListItemProps } from "./List";
import className from "~/kernel/styling/className";
import InputHelpText from "./InputHelpText";
import FieldError from "./FieldError";

type CheckboxGroupProps = {
  children: ReactNode;
};

export default function CheckboxGroup({ children }: CheckboxGroupProps) {
  return <ul>{children}</ul>;
}

type ItemLayout = Pick<ListItemProps, "compact" | "trim">;

const ItemLayoutContext = createContext<ItemLayout>({});

type ContainerProps = Pick<
  ListItemProps,
  "compact" | "transparent" | "trim"
> & {
  children: ReactNode;
};

function Container({ compact, transparent, trim, children }: ContainerProps) {
  return (
    <li
      className={className(
        "border-b border-slate-200 last:border-b-0",
        "grid grid-cols-[min-content_1fr] gap-2 items-center",
        { "opacity-0": transparent },
      )}
    >
      <ItemLayoutContext.Provider value={{ compact, trim }}>
        {children}
      </ItemLayoutContext.Provider>
    </li>
  );
}

type ControlProps = CheckboxProps & {
  helpText?: string;
};

function Control({ label, id, helpText, error, ...rest }: ControlProps) {
  const { compact, trim } = useContext(ItemLayoutContext);
  const labelClassName = className(
    "col-span-2 grid grid-cols-subgrid gap-2 items-center",
    "hover:bg-slate-100 w-full text-left cursor-pointer",
    {
      "pt-5 pb-5": !compact,
      "pt-3 pb-3": compact,
      "first:pt-0 last:pb-0": trim,
    },
  );

  return (
    <label htmlFor={id} className={labelClassName}>
      <Checkbox id={id} {...rest} />
      <div className={className({ "text-slate-400": rest.readOnly })}>
        {label}
      </div>
      {helpText ? (
        <>
          <div />
          <InputHelpText>{helpText}</InputHelpText>
        </>
      ) : null}
      {error ? (
        <>
          <div />
          <FieldError error={error} />
        </>
      ) : null}
    </label>
  );
}

type CellProps = {
  fullWidth?: boolean;
  children: ReactNode;
};

function Cell({ fullWidth = false, children }: CellProps) {
  const { compact, trim } = useContext(ItemLayoutContext);

  return (
    <div
      className={className({
        "col-span-2": fullWidth,
        "col-start-2": !fullWidth,
        "pb-5": !compact,
        "pb-3": compact,
        "last:pb-0": trim,
      })}
    >
      {children}
    </div>
  );
}

type CheckboxGroupItemProps = CheckboxProps &
  Pick<ListItemProps, "compact" | "transparent" | "trim" | "onClick"> & {
    helpText?: string;
  };

export function CheckboxGroupItem({
  compact,
  transparent,
  trim,
  ...rest
}: CheckboxGroupItemProps) {
  return (
    <Container compact={compact} transparent={transparent} trim={trim}>
      <Control {...rest} />
    </Container>
  );
}

CheckboxGroupItem.Container = Container;
CheckboxGroupItem.Control = Control;
CheckboxGroupItem.Cell = Cell;
